import type { Router } from "vue-router"

const RECOVERY_KEY = "pitchlane:dynamic-import-recovery"
const ASSET_PROBE_TIMEOUT_MS = 5_000
const DYNAMIC_IMPORT_CONTEXT = "dynamic_import_recovery"
const DYNAMIC_IMPORT_FINGERPRINT = "dashboard-dynamic-import"
const errorState = new WeakMap<object, "pending" | "handled" | "reported">()
let activeRecovery: object | undefined

export interface DynamicImportResourceTiming {
    initiator_type?: string
    duration_ms?: number
    transfer_size?: number
    encoded_body_size?: number
    decoded_body_size?: number
    next_hop_protocol?: string
    response_status?: number
    delivery_type?: string
}

interface RecoveryRecord {
    targetPath: string
    sourceRelease?: string
    attemptedAt: number
}

interface RecoveryDependencies {
    currentHref: string
    online: boolean
    fetchFn: typeof fetch
    storage: Storage
    assign: (targetPath: string) => void
    captureException: (error: unknown, hint: Record<string, unknown>) => void
    release?: string
    now?: () => number
    navigationSequence?: number
    currentNavigationSequence?: () => number
    clientSessionId?: string
    clientKind?: "bot" | "unknown"
    resourceTiming?: DynamicImportResourceTiming
}

export interface DynamicImportRecoveryRuntime {
    currentHref: () => string
    online: () => boolean
    fetchFn: typeof fetch
    storage: Storage
    assign: (targetPath: string) => void
    captureException: (error: unknown, hint: Record<string, unknown>) => void
    release?: string
    clientSessionId?: () => string | undefined
    clientKind?: () => "bot" | "unknown"
    resourceTiming?: (assetUrl: URL) => DynamicImportResourceTiming | undefined
    onResult?: (result: DynamicImportRecoveryResult, error: unknown) => void
}

export interface DynamicImportRecoveryResult {
    handled: boolean
    outcome:
        | "not-candidate"
        | "reloading"
        | "repeat-failure"
        | "asset-still-available"
        | "asset-response-invalid"
        | "asset-response-error"
        | "asset-probe-failed"
        | "guard-unavailable"
        | "superseded"
}

function getErrorMessage(error: unknown): string | undefined {
    if (!(error instanceof Error) || error.name !== "TypeError") return undefined
    return error.message
}

export function getDynamicImportAssetUrl(
    error: unknown,
    currentHref: string,
    online: boolean,
): URL | undefined {
    if (!online) return undefined

    const message = getErrorMessage(error)
    if (!message) return undefined

    const match = message.match(
        /^(?:Failed to fetch dynamically imported module|error loading dynamically imported module): (https?:\/\/\S+)$/i,
    )
    if (!match) return undefined

    let assetUrl: URL
    let currentUrl: URL
    try {
        assetUrl = new URL(match[1])
        currentUrl = new URL(currentHref)
    } catch {
        return undefined
    }

    if (assetUrl.origin !== currentUrl.origin) return undefined
    if (!/^\/assets\/.+-[A-Za-z0-9_-]{8,}\.js$/.test(assetUrl.pathname)) return undefined

    return assetUrl
}

export function markDynamicImportErrorPending(error: unknown): void {
    if (typeof error === "object" && error !== null) errorState.set(error, "pending")
}

function markDynamicImportError(error: unknown, state: "handled" | "reported"): void {
    if (typeof error === "object" && error !== null) errorState.set(error, state)
}

export function filterHandledDynamicImportEvent<T extends { contexts?: Record<string, unknown> }>(
    event: T,
    hint?: { originalException?: unknown },
): T | null {
    const original = hint?.originalException
    if (typeof original !== "object" || original === null || !errorState.has(original)) return event

    return event.contexts?.[DYNAMIC_IMPORT_CONTEXT] ? event : null
}

function readRecovery(storage: Storage): RecoveryRecord | undefined {
    const raw = storage.getItem(RECOVERY_KEY)
    if (!raw) return undefined

    try {
        const record = JSON.parse(raw) as RecoveryRecord
        if (typeof record.targetPath !== "string" || typeof record.attemptedAt !== "number") {
            storage.removeItem(RECOVERY_KEY)
            return undefined
        }
        return record
    } catch {
        storage.removeItem(RECOVERY_KEY)
        return undefined
    }
}

function reportFailure(
    error: unknown,
    assetUrl: URL,
    targetPath: string,
    outcome: Exclude<DynamicImportRecoveryResult["outcome"], "not-candidate" | "reloading">,
    dependencies: RecoveryDependencies,
    extra: Record<string, unknown> = {},
): DynamicImportRecoveryResult {
    markDynamicImportError(error, "reported")
    const failureCause =
        typeof extra.failure_cause === "string" ? extra.failure_cause : undefined
    dependencies.captureException(error, {
        fingerprint: [DYNAMIC_IMPORT_FINGERPRINT, outcome, failureCause ?? "unspecified"],
        tags: {
            dynamic_import_outcome: outcome,
            ...(failureCause ? { dynamic_import_failure_cause: failureCause } : {}),
            ...(dependencies.clientKind
                ? { dynamic_import_client_kind: dependencies.clientKind }
                : {}),
        },
        ...(dependencies.clientSessionId
            ? {
                  user: {
                      id: `dynamic-import-session:${dependencies.clientSessionId}`,
                  },
              }
            : {}),
        contexts: {
            [DYNAMIC_IMPORT_CONTEXT]: {
                outcome,
                asset_url: assetUrl.href,
                target_path: targetPath,
                current_route: new URL(dependencies.currentHref).pathname,
                release: dependencies.release ?? "unknown",
                client_kind: dependencies.clientKind,
                original_resource_timing: dependencies.resourceTiming,
                ...extra,
            },
        },
    })
    return { handled: true, outcome }
}

function supersede(error: unknown): DynamicImportRecoveryResult {
    markDynamicImportError(error, "handled")
    return { handled: true, outcome: "superseded" }
}

async function consumeResponseBody(response: Response): Promise<number> {
    if (!response.body) return (await response.arrayBuffer()).byteLength

    const reader = response.body.getReader()
    let bytes = 0
    while (true) {
        const { done, value } = await reader.read()
        if (done) return bytes
        bytes += value.byteLength
    }
}

export async function recoverFromStaleDynamicImport(
    error: unknown,
    targetPath: string,
    dependencies: RecoveryDependencies,
): Promise<DynamicImportRecoveryResult> {
    const assetUrl = getDynamicImportAssetUrl(error, dependencies.currentHref, dependencies.online)
    if (!assetUrl) return { handled: false, outcome: "not-candidate" }

    markDynamicImportErrorPending(error)

    const navigationIsCurrent = () =>
        dependencies.navigationSequence === undefined ||
        dependencies.currentNavigationSequence === undefined ||
        dependencies.navigationSequence === dependencies.currentNavigationSequence()
    if (!navigationIsCurrent()) return supersede(error)

    // Only the newest navigation may finish the one allowed recovery attempt.
    // Older probes retain their local token and become harmless when superseded.
    const recovery = {}
    activeRecovery = recovery

    let previous: RecoveryRecord | undefined
    try {
        previous = readRecovery(dependencies.storage)
    } catch (storageError) {
        if (activeRecovery === recovery) activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "guard-unavailable", dependencies, {
            storage_error: storageError instanceof Error ? storageError.name : "unknown",
        })
    }

    if (previous) {
        if (activeRecovery === recovery) activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "repeat-failure", dependencies, {
            source_release: previous.sourceRelease ?? "unknown",
            attempted_at: new Date(previous.attemptedAt).toISOString(),
            attempted_target: previous.targetPath,
        })
    }

    const probeController = new AbortController()
    let probeTimeout: ReturnType<typeof setTimeout> | undefined
    let response: Response
    let responseBodyBytes: number | undefined
    let probeStage: "fetch" | "body" = "fetch"
    try {
        const timeout = new Promise<never>((_, reject) => {
            probeTimeout = setTimeout(() => {
                probeController.abort()
                reject(new DOMException("Asset probe timed out", "TimeoutError"))
            }, ASSET_PROBE_TIMEOUT_MS)
        })
        const probe = dependencies
            .fetchFn(assetUrl, {
                method: "GET",
                cache: "no-store",
                credentials: "same-origin",
                redirect: "follow",
                signal: probeController.signal,
            })
            .then(async (probeResponse) => {
                probeStage = "body"
                return {
                    response: probeResponse,
                    bodyBytes: probeResponse.ok
                        ? await consumeResponseBody(probeResponse)
                        : undefined,
                }
            })
        const completedProbe = await Promise.race([probe, timeout])
        response = completedProbe.response
        responseBodyBytes = completedProbe.bodyBytes
    } catch (probeError) {
        if (activeRecovery !== recovery) return supersede(error)
        activeRecovery = undefined
        const probeErrorName =
            typeof probeError === "object" &&
            probeError !== null &&
            "name" in probeError &&
            typeof probeError.name === "string"
                ? probeError.name
                : "unknown"
        return reportFailure(error, assetUrl, targetPath, "asset-probe-failed", dependencies, {
            failure_cause:
                probeErrorName === "TimeoutError" ? "timeout" : `${probeStage}-${probeErrorName}`,
            probe_stage: probeStage,
            probe_error: probeErrorName,
        })
    } finally {
        if (probeTimeout) clearTimeout(probeTimeout)
    }

    if (activeRecovery !== recovery || !navigationIsCurrent()) return supersede(error)

    if (response.status === 404 || response.status === 410) {
        // Continue to the guarded one-time reload below.
    } else if (!response.ok) {
        activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "asset-response-error", dependencies, {
            failure_cause: `http-${Math.floor(response.status / 100)}xx`,
            probe_status: response.status,
        })
    } else {
        activeRecovery = undefined
        const contentType = response.headers.get("content-type")
        const responseUrlMatchesAsset = !response.url || response.url === assetUrl.href
        const hasJavaScriptMime =
            contentType !== null &&
            /^(?:application|text)\/(?:java|ecma)script(?:;|$)/i.test(contentType)
        const outcome =
            response.redirected || !responseUrlMatchesAsset || !hasJavaScriptMime
                ? "asset-response-invalid"
                : "asset-still-available"
        const failureCause = response.redirected
            ? "redirected"
            : !responseUrlMatchesAsset
              ? "url-mismatch"
              : !hasJavaScriptMime
                ? "invalid-mime"
                : "complete-javascript"
        return reportFailure(error, assetUrl, targetPath, outcome, dependencies, {
            failure_cause: failureCause,
            probe_status: response.status,
            probe_content_type: contentType ?? "missing",
            probe_response_type: response.type,
            probe_redirected: response.redirected,
            probe_url_matches_asset: responseUrlMatchesAsset,
            probe_body_bytes: responseBodyBytes,
        })
    }

    const now = (dependencies.now ?? Date.now)()
    try {
        dependencies.storage.setItem(
            RECOVERY_KEY,
            JSON.stringify({
                targetPath,
                sourceRelease: dependencies.release,
                attemptedAt: now,
            } satisfies RecoveryRecord),
        )
    } catch (storageError) {
        activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "guard-unavailable", dependencies, {
            storage_error: storageError instanceof Error ? storageError.name : "unknown",
        })
    }

    activeRecovery = undefined
    markDynamicImportError(error, "handled")
    dependencies.assign(targetPath)
    return { handled: true, outcome: "reloading" }
}

export function clearSuccessfulDynamicImportRecovery(
    targetPath: string,
    storage: Storage,
    navigationFailure?: unknown,
): void {
    if (navigationFailure) return

    try {
        const record = readRecovery(storage)
        if (record?.targetPath === targetPath) storage.removeItem(RECOVERY_KEY)
    } catch {
        // A storage failure must not interfere with successful navigation.
    }
}

export function classifyDynamicImportClient(userAgent: string): "bot" | "unknown" {
    return /(?:bot|spider|crawler|slurp|headless|facebookexternalhit|google-inspectiontool)/i.test(
        userAgent,
    )
        ? "bot"
        : "unknown"
}

export function getDynamicImportResourceTiming(
    performance: Performance,
    assetUrl: URL,
): DynamicImportResourceTiming | undefined {
    let entries: PerformanceEntry[]
    try {
        entries = performance.getEntriesByName(assetUrl.href, "resource")
    } catch {
        return undefined
    }
    const entry = entries[entries.length - 1] as
        | (PerformanceResourceTiming & { responseStatus?: number; deliveryType?: string })
        | undefined
    if (!entry) return undefined

    return {
        initiator_type: entry.initiatorType || undefined,
        duration_ms: Math.round(entry.duration),
        transfer_size: entry.transferSize,
        encoded_body_size: entry.encodedBodySize,
        decoded_body_size: entry.decodedBodySize,
        next_hop_protocol: entry.nextHopProtocol || undefined,
        response_status: entry.responseStatus,
        delivery_type: entry.deliveryType || undefined,
    }
}

export function installDynamicImportRecovery(
    router: Router,
    runtime: DynamicImportRecoveryRuntime,
): void {
    const navigationsSeenByBeforeEach = new WeakSet<object>()
    const navigationSequences = new WeakMap<object, number>()
    let currentNavigationSequence = 0

    router.beforeEach((to) => {
        currentNavigationSequence += 1
        navigationsSeenByBeforeEach.add(to)
        navigationSequences.set(to, currentNavigationSequence)
        // Any newer navigation makes a pending probe obsolete, even when the
        // newer navigation succeeds or fails for a different reason.
        activeRecovery = undefined
    })

    router.afterEach((to, _from, failure) => {
        if (navigationsSeenByBeforeEach.has(to)) {
            // This navigation already invalidated older probes when it began.
            // Do not let its late completion invalidate a newer navigation.
            navigationsSeenByBeforeEach.delete(to)
        } else {
            // Duplicate navigation can skip beforeEach, but is still newer
            // user intent and must supersede an older pending probe.
            currentNavigationSequence += 1
            activeRecovery = undefined
        }
        clearSuccessfulDynamicImportRecovery(to.fullPath, runtime.storage, failure)
    })

    router.onError((error, to) => {
        const navigationSequence = navigationSequences.get(to)
        if (navigationSequence === undefined) {
            console.error("Uncaught Navigation Error", error)
            return
        }

        const currentHref = runtime.currentHref()
        const online = runtime.online()
        const assetUrl = getDynamicImportAssetUrl(error, currentHref, online)
        let clientSessionId: string | undefined
        let clientKind: "bot" | "unknown" | undefined
        let resourceTiming: DynamicImportResourceTiming | undefined
        try {
            clientSessionId = runtime.clientSessionId?.()
        } catch {
            // Diagnostic enrichment must never interfere with recovery.
        }
        try {
            clientKind = runtime.clientKind?.()
        } catch {
            // Diagnostic enrichment must never interfere with recovery.
        }
        try {
            resourceTiming = assetUrl ? runtime.resourceTiming?.(assetUrl) : undefined
        } catch {
            // Diagnostic enrichment must never interfere with recovery.
        }
        void recoverFromStaleDynamicImport(error, to.fullPath, {
            currentHref,
            online,
            fetchFn: runtime.fetchFn,
            storage: runtime.storage,
            assign: runtime.assign,
            captureException: runtime.captureException,
            release: runtime.release,
            navigationSequence,
            currentNavigationSequence: () => currentNavigationSequence,
            clientSessionId,
            clientKind,
            resourceTiming,
        }).then((result) => {
            runtime.onResult?.(result, error)
            if (!result.handled) console.error("Uncaught Navigation Error", error)
        })
    })
}
